/**
 * Custom React Hook for Post Type Management
 * Provides comprehensive post type functionality with validation and auto-detection
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PostType,
  PostTypeConfig,
  PostTypeValidationResult,
  EnhancedFileUploadRequest,
  EnhancedFileUploadResponse,
  PostTypeError,
  FileValidationError,
  EnhancedArticlePayload
} from '../types/postTypes';
import postTypeService from '../services/postTypeService';

// Hook return type for usePostTypes
export interface UsePostTypesReturn {
  postTypes: Record<PostType, PostTypeConfig>;
  postTypeList: PostTypeConfig[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getPostTypeConfig: (postType: PostType) => PostTypeConfig | null;
  getDisplayInfo: (postType: PostType) => { name: string; description: string; icon?: string } | null;
}

// Hook return type for usePostTypeValidation
export interface UsePostTypeValidationReturn {
  validateFile: (file: File, postType: PostType, fileCategory?: string) => PostTypeValidationResult;
  validateArticle: (payload: EnhancedArticlePayload) => PostTypeValidationResult;
  detectPostType: (
    title: string,
    description: string,
    body: string,
    files: File[],
    subArticleCount: number
  ) => PostType;
  getFileExtensionRules: (postType: PostType) => { allowed: string[]; maxSize: number } | null;
  supportsMultipleSubArticles: (postType: PostType) => boolean;
}

// Hook return type for useFileUpload
export interface UseFileUploadReturn {
  uploadFile: (request: EnhancedFileUploadRequest) => Promise<EnhancedFileUploadResponse>;
  isUploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
  clearError: () => void;
}

// Hook return type for usePostTypeArticles
export interface UsePostTypeArticlesReturn {
  createArticle: (payload: EnhancedArticlePayload) => Promise<any>;
  updateArticle: (id: string, payload: EnhancedArticlePayload) => Promise<any>;
  getArticle: (id: string) => Promise<any>;
  isProcessing: boolean;
  processingError: string | null;
  clearProcessingError: () => void;
}

/**
 * Hook for managing post types configuration and data
 */
export const usePostTypes = (): UsePostTypesReturn => {
  const [postTypes, setPostTypes] = useState<Record<PostType, PostTypeConfig>>({} as Record<PostType, PostTypeConfig>);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPostTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await postTypeService.getPostTypes();
      setPostTypes(data);
    } catch (err: any) {
      console.error('Error fetching post types:', err);
      setError(err.message || 'Failed to load post types');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPostTypes();
  }, [fetchPostTypes]);

  const postTypeList = useMemo(() => {
    return Object.values(postTypes);
  }, [postTypes]);

  const getPostTypeConfig = useCallback((postType: PostType): PostTypeConfig | null => {
    return postTypes[postType] || null;
  }, [postTypes]);

  const getDisplayInfo = useCallback((postType: PostType) => {
    try {
      return postTypeService.getPostTypeDisplayInfo(postType);
    } catch {
      return null;
    }
  }, []);

  return {
    postTypes,
    postTypeList,
    isLoading,
    error,
    refetch: fetchPostTypes,
    getPostTypeConfig,
    getDisplayInfo
  };
};

/**
 * Hook for post type validation operations
 */
export const usePostTypeValidation = (): UsePostTypeValidationReturn => {
  const validateFile = useCallback((file: File, postType: PostType, fileCategory?: string): PostTypeValidationResult => {
    try {
      return postTypeService.validateFile(file, postType, fileCategory || 'file');
    } catch (error: any) {
      return {
        isValid: false,
        errors: [error.message || 'File validation failed'],
        warnings: []
      };
    }
  }, []);

  const validateArticle = useCallback((payload: EnhancedArticlePayload): PostTypeValidationResult => {
    try {
      return postTypeService.validateArticlePayload(payload);
    } catch (error: any) {
      return {
        isValid: false,
        errors: [error.message || 'Article validation failed'],
        warnings: []
      };
    }
  }, []);

  const detectPostType = useCallback((
    title: string,
    description: string,
    body: string,
    files: File[],
    subArticleCount: number
  ): PostType => {
    try {
      return postTypeService.detectPostType(title, description, body, files, subArticleCount);
    } catch {
      return PostType.NORMAL_POST;
    }
  }, []);

  const getFileExtensionRules = useCallback((postType: PostType) => {
    try {
      return postTypeService.getFileExtensionRules(postType);
    } catch {
      return null;
    }
  }, []);

  const supportsMultipleSubArticles = useCallback((postType: PostType): boolean => {
    try {
      return postTypeService.supportsMultipleSubArticles(postType);
    } catch {
      return true; // Default to true for unknown post types
    }
  }, []);

  return {
    validateFile,
    validateArticle,
    detectPostType,
    getFileExtensionRules,
    supportsMultipleSubArticles
  };
};

/**
 * Hook for enhanced file upload with post type support
 */
export const useFileUpload = (): UseFileUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFile = useCallback(async (request: EnhancedFileUploadRequest): Promise<EnhancedFileUploadResponse> => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);

      const response = await postTypeService.uploadFile(request);

      clearInterval(progressInterval);
      setUploadProgress(100);

      return response;
    } catch (error: any) {
      setUploadError(error.message || 'Upload failed');
      throw error;
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, []);

  const clearError = useCallback(() => {
    setUploadError(null);
  }, []);

  return {
    uploadFile,
    isUploading,
    uploadProgress,
    uploadError,
    clearError
  };
};

/**
 * Hook for post type article operations
 */
export const usePostTypeArticles = (): UsePostTypeArticlesReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);

  const createArticle = useCallback(async (payload: EnhancedArticlePayload) => {
    try {
      setIsProcessing(true);
      setProcessingError(null);
      return await postTypeService.createArticle(payload);
    } catch (error: any) {
      const errorMessage = error instanceof PostTypeError 
        ? `${error.message}${error.validationErrors ? ': ' + error.validationErrors.join(', ') : ''}`
        : error.message || 'Failed to create article';
      setProcessingError(errorMessage);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const updateArticle = useCallback(async (id: string, payload: EnhancedArticlePayload) => {
    try {
      setIsProcessing(true);
      setProcessingError(null);
      return await postTypeService.updateArticle(id, payload);
    } catch (error: any) {
      const errorMessage = error instanceof PostTypeError 
        ? `${error.message}${error.validationErrors ? ': ' + error.validationErrors.join(', ') : ''}`
        : error.message || 'Failed to update article';
      setProcessingError(errorMessage);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const getArticle = useCallback(async (id: string) => {
    try {
      setProcessingError(null);
      return await postTypeService.getArticle(id);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch article';
      setProcessingError(errorMessage);
      throw error;
    }
  }, []);

  const clearProcessingError = useCallback(() => {
    setProcessingError(null);
  }, []);

  return {
    createArticle,
    updateArticle,
    getArticle,
    isProcessing,
    processingError,
    clearProcessingError
  };
};

/**
 * Combined hook that provides all post type functionality
 */
export interface UsePostTypeSystemReturn extends 
  UsePostTypesReturn,
  UsePostTypeValidationReturn,
  UseFileUploadReturn,
  UsePostTypeArticlesReturn {
  // Additional combined functionality
  isInitialized: boolean;
  hasError: boolean;
  allErrors: string[];
  clearAllErrors: () => void;
}

export const usePostTypeSystem = (): UsePostTypeSystemReturn => {
  const postTypes = usePostTypes();
  const validation = usePostTypeValidation();
  const fileUpload = useFileUpload();
  const articles = usePostTypeArticles();

  const isInitialized = useMemo(() => {
    return !postTypes.isLoading;
  }, [postTypes.isLoading]);

  const allErrors = useMemo(() => {
    const errors: string[] = [];
    if (postTypes.error) errors.push(postTypes.error);
    if (fileUpload.uploadError) errors.push(fileUpload.uploadError);
    if (articles.processingError) errors.push(articles.processingError);
    return errors;
  }, [postTypes.error, fileUpload.uploadError, articles.processingError]);

  const hasError = useMemo(() => {
    return allErrors.length > 0;
  }, [allErrors]);

  const clearAllErrors = useCallback(() => {
    fileUpload.clearError();
    articles.clearProcessingError();
  }, [fileUpload, articles]);

  return {
    ...postTypes,
    ...validation,
    ...fileUpload,
    ...articles,
    isInitialized,
    hasError,
    allErrors,
    clearAllErrors
  };
};

export default usePostTypeSystem;
